import { useState } from 'react'
import { useAccountSettings, useUpdateAccountSettings } from '@/hooks/useAccountSettings'
import { useApiErrorTranslator } from '@/hooks/useApiErrorTranslator'
import type { SettingsSaveError } from '@/lib/settingsSaveError'

export const DEFAULT_CARRIERS = [
  'Thailand Post',
  'Kerry Express',
  'Flash Express',
  'J&T Express',
  'Shopee Express',
  'Lazada Express',
]

export function useCarrierSettings() {
  const { data: account } = useAccountSettings()
  const updateSettings = useUpdateAccountSettings()
  const translateError = useApiErrorTranslator('settings')
  // PAY-1 (T149-084): no client validation here (none invented), so this is
  // only ever set on a server failure. Cleared on the next save attempt.
  const [saveError, setSaveError] = useState<SettingsSaveError | null>(null)

  // null means "not yet edited by user" — use server data
  const [localCarriers, setLocalCarriers] = useState<string[] | null>(null)
  const [newCarrier, setNewCarrier] = useState('')

  // Derive current carriers: prefer local edits, fall back to server data
  const serverCarriers =
    account?.settings.parcel_carriers?.length
      ? account.settings.parcel_carriers
      : DEFAULT_CARRIERS
  const carriers = localCarriers ?? serverCarriers

  const setCarriers = (updater: string[] | ((prev: string[]) => string[])) => {
    setLocalCarriers((prev) => {
      const current = prev ?? serverCarriers
      return typeof updater === 'function' ? updater(current) : updater
    })
  }

  const addCarrier = () => {
    const trimmed = newCarrier.trim()
    if (!trimmed || carriers.includes(trimmed)) return
    setCarriers((prev) => [...prev, trimmed])
    setNewCarrier('')
  }

  const removeCarrier = (index: number) => {
    setCarriers((prev) => prev.filter((_, i) => i !== index))
  }

  const resetCarriers = () => {
    setLocalCarriers(DEFAULT_CARRIERS)
  }

  // PAY-1 (T149-084): returns true only when the save actually happened.
  // The caller shows success only on true.
  const handleSave = async (): Promise<boolean> => {
    setSaveError(null)
    try {
      await updateSettings.mutateAsync({
        settings: { parcel_carriers: carriers },
      })
      return true
    } catch (err) {
      setSaveError({ serverMessage: translateError(err) })
      return false
    }
  }

  return {
    carriers,
    newCarrier,
    setNewCarrier,
    isPending: updateSettings.isPending,
    saveError,
    addCarrier,
    removeCarrier,
    resetCarriers,
    handleSave,
  }
}
